import { useCallback, useEffect, useState } from 'react';
import { Handshake, Languages, Plus, Power } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inp = 'h-9 px-3 rounded-lg bg-white/[0.06] border border-white/15 text-sm text-white placeholder:text-white/35';
const LANGS = [['fr', 'FR *'], ['en', 'EN'], ['es', 'ES'], ['gcf', 'KRÉYÒL']];
const STATUS = {
  NOUVELLE: 'bg-[#60A5FA]/15 text-[#60A5FA]', EN_COURS: 'bg-[#D9B35A]/20 text-[#E9CF8E]',
  ACCEPTEE: 'bg-emerald-500/15 text-emerald-400', REFUSEE: 'bg-red-500/15 text-red-400',
};

export const PartnerApplicationsPanel = () => {
  const [apps, setApps] = useState([]);
  const [types, setTypes] = useState([]);
  const [newType, setNewType] = useState({ code: '', fr: '', en: '', es: '', gcf: '' });
  const [editing, setEditing] = useState(null);
  const [editLabels, setEditLabels] = useState({});

  const load = useCallback(() => {
    fetch(`${API}/admin/partners/applications`, { credentials: 'include' })
      .then((r) => r.json()).then((d) => setApps(d.items || [])).catch(() => {});
    fetch(`${API}/admin/partners/types`, { credentials: 'include' })
      .then((r) => r.json()).then((d) => setTypes(d.items || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (a, status) => {
    const r = await fetch(`${API}/admin/partners/applications/${a.id}`, {
      method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });
    if (!r.ok) return toast.error('Erreur');
    toast.success(`Candidature ${status.toLowerCase().replace('_', ' ')}`);
    load();
  };

  const addType = async () => {
    if (!newType.code.trim() || !newType.fr.trim()) return toast.error('Code et libellé FR requis');
    const r = await fetch(`${API}/admin/partners/types`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code: newType.code, label: newType.fr,
        labels: { fr: newType.fr, en: newType.en, es: newType.es, gcf: newType.gcf } }),
    });
    const d = await r.json();
    if (!r.ok) return toast.error(d.detail || 'Erreur');
    toast.success(`Espace « ${d.label} » ajouté au formulaire partenaire`);
    setNewType({ code: '', fr: '', en: '', es: '', gcf: '' });
    load();
  };

  const openEdit = (t) => {
    setEditing(t.id);
    setEditLabels({ fr: t.labels?.fr || t.label || '', en: t.labels?.en || '', es: t.labels?.es || '', gcf: t.labels?.gcf || '' });
  };

  const saveLabels = async () => {
    if (!editLabels.fr?.trim()) return toast.error('Le libellé FR est requis');
    const r = await fetch(`${API}/admin/partners/types/${editing}/labels`, {
      method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ labels: editLabels }),
    });
    const d = await r.json();
    if (!r.ok) return toast.error(d.detail || 'Erreur');
    toast.success('Traductions enregistrées');
    setEditing(null);
    load();
  };

  const toggleType = async (t) => {
    await fetch(`${API}/admin/partners/types/${t.id}`, { method: 'PATCH', credentials: 'include' });
    load();
  };

  return (
    <div className="glass-panel-soft rounded-[18px] p-5" data-testid="partner-apps-panel">
      <h3 className="font-display text-lg mb-1 text-white flex items-center gap-2">
        <Handshake size={16} style={{ color: '#D9B35A' }} /> Devenir partenaire — candidatures
        <span className="text-sm font-normal text-white/50">({apps.length})</span>
      </h3>
      <p className="text-xs text-white/45 mb-4">Demandes reçues via le formulaire du pied de page. Gérez aussi les espaces proposés dans le formulaire.</p>

      <div className="p-3 rounded-xl bg-white/[0.03] border border-white/[0.06] mb-4 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          {types.map((t) => (
            <span key={t.id} className="inline-flex items-center">
              <button type="button" onClick={() => toggleType(t)} data-testid={`partner-type-${t.code}`}
                title={t.active ? 'Cliquer pour masquer du formulaire' : 'Cliquer pour réafficher'}
                className={`px-2 py-1 rounded-l-lg text-[10px] font-bold border inline-flex items-center gap-1 transition-colors ${t.active ? 'bg-[#D9B35A]/20 text-[#E9CF8E] border-[#D9B35A]/40' : 'bg-white/[0.04] text-white/35 border-white/10 line-through'}`}>
                <Power size={10} /> {t.label}
              </button>
              <button type="button" onClick={() => (editing === t.id ? setEditing(null) : openEdit(t))}
                data-testid={`partner-type-${t.code}-translate`} title="Traduire ce type (FR/EN/ES/Kréyòl)"
                className={`px-1.5 py-1 rounded-r-lg border border-l-0 transition-colors ${editing === t.id ? 'bg-[#D9B35A]/30 text-[#E9CF8E] border-[#D9B35A]/40' : 'bg-white/[0.04] text-white/40 border-white/10 hover:text-[#E9CF8E]'}`}>
                <Languages size={11} />
              </button>
            </span>
          ))}
        </div>
        {editing && (
          <div className="flex flex-wrap items-center gap-2 p-2 rounded-lg bg-[#D9B35A]/[0.06] border border-[#D9B35A]/20" data-testid="partner-type-labels-editor">
            {LANGS.map(([k, lbl]) => (
              <label key={k} className="flex items-center gap-1.5 text-[10px] font-bold text-white/50">
                {lbl}
                <input value={editLabels[k] || ''} onChange={(e) => setEditLabels({ ...editLabels, [k]: e.target.value })}
                  data-testid={`partner-type-label-${k}`} className={`${inp} w-44 font-normal`} />
              </label>
            ))}
            <button type="button" onClick={saveLabels} data-testid="partner-type-labels-save"
              className="btn-gold h-9 px-3 rounded-lg text-xs font-semibold">Enregistrer</button>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <input value={newType.code} onChange={(e) => setNewType({ ...newType, code: e.target.value })} placeholder="CODE" data-testid="partner-type-code-input" className={`${inp} w-24`} />
          <input value={newType.fr} onChange={(e) => setNewType({ ...newType, fr: e.target.value })} placeholder="Libellé FR *" data-testid="partner-type-label-input" className={`${inp} flex-1 min-w-[130px]`} />
          <input value={newType.en} onChange={(e) => setNewType({ ...newType, en: e.target.value })} placeholder="EN" data-testid="partner-type-en-input" className={`${inp} w-32`} />
          <input value={newType.es} onChange={(e) => setNewType({ ...newType, es: e.target.value })} placeholder="ES" data-testid="partner-type-es-input" className={`${inp} w-32`} />
          <input value={newType.gcf} onChange={(e) => setNewType({ ...newType, gcf: e.target.value })} placeholder="Kréyòl" data-testid="partner-type-gcf-input" className={`${inp} w-32`} />
          <button type="button" onClick={addType} data-testid="partner-type-add-btn"
            className="btn-gold h-9 px-3 rounded-lg text-xs font-semibold inline-flex items-center gap-1"><Plus size={13} /> Ajouter</button>
        </div>
      </div>

      <div className="space-y-2">
        {apps.map((a) => (
          <div key={a.id} className="p-3 rounded-xl bg-white/[0.03] border border-white/[0.06]" data-testid={`partner-app-${a.id}`}>
            <div className="flex flex-wrap items-center gap-2">
              <span className="px-2 py-0.5 rounded-lg text-[9px] font-bold bg-white/10 text-white/70 uppercase">{a.type}</span>
              <p className="text-sm font-bold text-white flex-1 min-w-[140px]">{a.name}{a.company ? ` — ${a.company}` : ''}</p>
              <span className="text-xs text-white/45">{a.email}{a.phone ? ` · ${a.phone}` : ''}</span>
              <span className={`px-2 py-0.5 rounded-lg text-[9px] font-bold ${STATUS[a.status] || STATUS.NOUVELLE}`} data-testid={`partner-app-status-${a.id}`}>{a.status.replace('_', ' ')}</span>
            </div>
            {a.message && <p className="text-xs text-white/55 mt-1.5">{a.message}</p>}
            <div className="flex gap-1.5 mt-2">
              {['EN_COURS', 'ACCEPTEE', 'REFUSEE'].filter((s) => s !== a.status).map((s) => (
                <button key={s} type="button" onClick={() => setStatus(a, s)} data-testid={`partner-app-${a.id}-set-${s}`}
                  className="px-2 py-1 rounded-lg text-[10px] font-bold bg-white/[0.06] border border-white/15 text-white/70 hover:text-white transition-colors">
                  {s === 'EN_COURS' ? 'En cours' : s === 'ACCEPTEE' ? 'Accepter' : 'Refuser'}
                </button>
              ))}
            </div>
          </div>
        ))}
        {!apps.length && <p className="text-sm text-white/45 py-3">Aucune candidature pour l'instant.</p>}
      </div>
    </div>
  );
};
